import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout } from "@/components/LegalLayout";

export const Route = createFileRoute("/legal/contractor-agreement")({
  head: () => ({
    meta: [
      { title: "Independent Contractor Agreement — The Hospitality Reserve" },
      {
        name: "description",
        content:
          "Standard independent-contractor terms accepted by bartenders engaged through The Hospitality Reserve register.",
      },
      { property: "og:title", content: "Independent Contractor Agreement — The Hospitality Reserve" },
      { property: "og:url", content: "https://thehospitalityreserve.com/legal/contractor-agreement" },
    ],
    links: [{ rel: "canonical", href: "https://thehospitalityreserve.com/legal/contractor-agreement" }],
  }),
  component: ContractorPage,
});

function ContractorPage() {
  return (
    <LegalLayout
      eyebrow="Legal"
      title="Independent Contractor Agreement"
      updated="18 August 2026"
    >
      <p>
        This agreement is entered into by each Bartender ("<strong>Contractor</strong>")
        on registration and applies to every engagement accepted through The
        The Hospitality Reserve Platform.
      </p>

      <h2>1. Status</h2>
      <ul>
        <li>
          The Contractor is engaged as a <strong>self-employed independent
          contractor</strong> in business on their own account.
        </li>
        <li>
          Nothing in this agreement, or in any booking accepted via the
          Platform, creates a relationship of employer/employee, worker,
          agency worker, partnership, or joint venture between the Contractor
          and the Platform.
        </li>
        <li>
          <strong>The Platform is not an employment business or employment
          agency.</strong> It provides software that enables the Contractor
          and the Venue to find each other and transact. The contract for
          services for each engagement is formed directly between the
          Contractor and the Venue.
        </li>
      </ul>

      <h2>2. Tax, NI and VAT</h2>
      <p>
        The Contractor is solely responsible for accounting to HM Revenue &
        Customs for all income tax, National Insurance contributions, and (if
        registered) VAT in respect of fees received. The Contractor indemnifies
        the Platform against any liability for income tax, NICs, penalties or
        interest arising from their engagements.
      </p>

      <h2>3. Substitution and control</h2>
      <p>
        The Contractor may, with the Venue's prior agreement, send a suitably
        qualified substitute. The Contractor provides their own tools of trade
        where reasonable, exercises their own professional judgement, and is
        not subject to the day-to-day control of the Platform.
      </p>

      <h2>4. Insurance</h2>
      <p>
        The Contractor is responsible for maintaining appropriate public
        liability insurance and any other insurance reasonably required for
        their profession.
      </p>

      <h2>5. Compliance</h2>
      <p>
        The Contractor will comply with all applicable laws including the
        Licensing Act 2003, health and safety obligations, food hygiene
        standards, and the Venue's reasonable house rules while on site.
      </p>

      <h2>6. Right to work</h2>
      <p>
        The Contractor warrants that they have the legal right to work in the
        United Kingdom and will provide documentary evidence on request. The
        Platform may suspend the account where evidence is not provided or has
        expired.
      </p>

      <h2>7. Confidentiality</h2>
      <p>
        The Contractor will keep confidential any non-public information about
        the Venue, its members, guests, and operations obtained in the course
        of an engagement.
      </p>

      <h2>8. Off-Platform engagements</h2>
      <p>
        Where the Contractor and Venue first met through the Platform, the
        Contractor will not accept off-Platform casual or shift-based
        engagements with that Venue for a period of 6 months from the last
        Platform booking, save with the Platform's written consent.
      </p>
      <p>
        <strong>
          This does not restrict permanent or ongoing employment.
        </strong>{" "}
        A Venue may offer, and a Contractor may accept, a permanent, fixed-term,
        or direct employment role at any time. No introduction fee, release fee,
        transfer fee, or commission of any kind is payable to the Platform in
        that event.
      </p>


      <h2>9. Liability</h2>
      <p>
        The Contractor accepts liability for their own acts and omissions and
        indemnifies the Platform against third-party claims arising from them.
        Nothing limits liability for death, personal injury caused by
        negligence, or fraud.
      </p>

      <h2>10. Gross misconduct and immediate termination</h2>
      <p>
        The Venue manager or owner reserves the right to{" "}
        <strong>terminate an engagement immediately and without notice</strong>{" "}
        where the Contractor commits an act of gross misconduct. Gross misconduct
        includes, but is not limited to, theft or dishonesty, physical violence
        or threats of violence, verbal abuse, harassment or discrimination,
        intentional or reckless damage to property, and attending or working
        under the influence of alcohol or unlawful drugs.
      </p>

      <h2>11. Payment where an engagement is terminated for misconduct</h2>
      <ul>
        <li>
          The Contractor is eligible for payment{" "}
          <strong>only for the hours actually worked up to the exact time of
          termination</strong>, calculated to the minute and subject to review. No
          fee is payable for the unworked remainder of the engagement.
        </li>
        <li>
          The Platform prorates the engagement value to the recorded termination
          time automatically, and adjusts the amount charged to the Venue and the
          amount transferred to the Contractor accordingly.
        </li>
        <li>
          <strong>
            The Platform may hold or freeze payout funds for the duration of an
            active dispute investigation
          </strong>{" "}
          involving a severe policy violation, alleged illegal activity, or a
          safety incident. Funds are released, adjusted, or withheld once the
          investigation concludes. Where theft or fraud is substantiated, payment
          may be withheld in full and the Venue refunded.
        </li>
        <li>
          The Contractor may make written representations during an investigation
          via{" "}
          <a href="mailto:admin@thehospitalityreserve.com">admin@thehospitalityreserve.com</a>.
        </li>
      </ul>

      <h2>12. Permanent bans and cooperation with law enforcement</h2>
      <ul>
        <li>
          Any substantiated claim of physical violence, theft, or other illegal
          conduct results in an{" "}
          <strong>immediate, permanent ban</strong> from the Platform, and a
          prohibition on creating any further account.
        </li>
        <li>
          The account may be frozen without notice while a serious allegation is
          investigated.
        </li>
        <li>
          The Contractor acknowledges and agrees that The Hospitality Reserve{" "}
          <strong>
            may cooperate fully with the police and other law enforcement or
            regulatory authorities
          </strong>
          , including by disclosing identity verification details, right-to-work
          records, booking and payment records, messages, incident reports, and
          supporting evidence in connection with a criminal investigation or the
          prevention or detection of crime.
        </li>
      </ul>

      <h2>13. Governing law</h2>
      <p>
        This agreement is governed by the laws of England and Wales.
      </p>
    </LegalLayout>
  );
}
